import {
  createLayoutMockInput,
  findLayoutMock,
  issuePageCount,
  layoutMockCatalog,
  renderLayoutMock,
  renderIssueDocument,
  templateManifests,
} from '@tsuzuri/templates';
import type { LayoutMockCatalogItem, LayoutMockId } from '@tsuzuri/templates';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Shell } from '../components';

type TemplateId = LayoutMockCatalogItem['template'];
type FormatId = LayoutMockCatalogItem['format'];

function readInitialSelection(): { template: TemplateId; format: FormatId } {
  const params = new URLSearchParams(window.location.search);
  const requestedTemplate = params.get('template');
  const requestedFormat = params.get('format');
  const requested = findLayoutMock(`${requestedTemplate}--${requestedFormat}`);
  if (requested) return { template: requested.template, format: requested.format };
  const requestedTemplatePreview = layoutMockCatalog.find(
    (item) => item.template === requestedTemplate && item.format === 'preview_jpeg',
  );
  const fallback = requestedTemplatePreview ?? layoutMockCatalog[0];
  if (!fallback) throw new Error('Layout mock catalog is empty');
  return { template: fallback.template, format: fallback.format };
}

function toCssPixels(value: number, unit: 'mm' | 'px') {
  return unit === 'px' ? value : (value * 96) / 25.4;
}

export function MockupsPage() {
  const initial = useMemo(readInitialSelection, []);
  const [template, setTemplate] = useState<TemplateId>(initial.template);
  const [format, setFormat] = useState<FormatId>(initial.format);
  const [showBrand, setShowBrand] = useState(true);
  const [stageWidth, setStageWidth] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLIFrameElement>(null);
  const manifest = templateManifests[template];
  const mockId = `${template}--${format}` as LayoutMockId;
  const catalogItem = findLayoutMock(mockId);
  if (!catalogItem) throw new Error(`Layout mock was not found: ${mockId}`);
  const profile = catalogItem.profile;
  const canvasWidth = toCssPixels(profile.width, profile.unit);
  const pageHeight = toCssPixels(profile.height, profile.unit);
  const fallbackWidth = Math.min(canvasWidth, 860);
  const scale = (stageWidth || fallbackWidth) / canvasWidth;
  // PDF は表紙面のうしろに本文ページが続く。ステージが1ページ分の高さのままだと
  // 2ページ目以降が overflow:hidden に切られ、目視確認できなくなる。
  const pageCount = useMemo(() => issuePageCount(createLayoutMockInput(mockId)), [mockId]);
  const canvasHeight = pageHeight * pageCount;

  const mockDocument = useMemo(() => {
    if (showBrand) return renderLayoutMock(mockId);
    const input = createLayoutMockInput(mockId);
    input.showBrand = false;
    return renderIssueDocument(input);
  }, [mockId, showBrand]);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const updateWidth = () => setStageWidth(stage.getBoundingClientRect().width);
    updateWidth();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('template', template);
    url.searchParams.set('format', format);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [format, template]);

  function selectTemplate(nextTemplate: TemplateId) {
    setTemplate(nextTemplate);
    if (!findLayoutMock(`${nextTemplate}--${format}`)) {
      setFormat('preview_jpeg');
    }
  }

  function openDocument() {
    const url = URL.createObjectURL(new Blob([mockDocument], { type: 'text/html;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  function printDocument() {
    previewRef.current?.contentWindow?.focus();
    previewRef.current?.contentWindow?.print();
  }

  return (
    <Shell>
      <main className="mockups-page">
        <header className="mockups-intro">
          <div>
            <p className="eyebrow">LAYOUT MOCKUPS</p>
            <h1>紙面レイアウト見本</h1>
            <p>
              アプリを起動せず、実際の発行処理と同じテンプレートで新聞・雑誌などの紙面を確認できます。
            </p>
          </div>
          <p className="mockups-version">
            TEMPLATE <b>v{manifest.version}</b>
          </p>
        </header>

        <section className="mockups-workspace" aria-label="紙面モックプレビュー">
          <aside className="mockups-controls">
            <div className="mockups-field">
              <label htmlFor="mockup-template">テンプレート</label>
              <select
                id="mockup-template"
                value={template}
                onChange={(event) => selectTemplate(event.currentTarget.value as TemplateId)}
              >
                {Object.values(templateManifests).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.displayName}
                  </option>
                ))}
              </select>
              <small>{manifest.description}</small>
            </div>

            <div className="mockups-field">
              <label htmlFor="mockup-format">出力形式</label>
              <select
                id="mockup-format"
                value={format}
                onChange={(event) => setFormat(event.currentTarget.value as FormatId)}
              >
                {layoutMockCatalog
                  .filter((item) => item.template === template)
                  .map((item) => (
                    <option key={item.id} value={item.format}>
                      {item.formatName}
                    </option>
                  ))}
              </select>
              <small>
                {profile.width} × {profile.height} {profile.unit} · {profile.dpi} dpi
              </small>
            </div>

            <label className="mockups-toggle">
              <input
                type="checkbox"
                checked={showBrand}
                onChange={(event) => setShowBrand(event.currentTarget.checked)}
              />
              <span>フッターのメッセージを表示</span>
            </label>

            <dl className="mockups-meta">
              <div>
                <dt>推奨できごと数</dt>
                <dd>
                  {manifest.recommendedEventCount.min}–{manifest.recommendedEventCount.max}件
                </dd>
              </div>
              <div>
                <dt>安全マージン</dt>
                <dd>
                  {profile.safeMargin} {profile.unit}
                </dd>
              </div>
            </dl>

            <div className="mockups-actions">
              <button className="button primary" type="button" onClick={openDocument}>
                別タブで開く
              </button>
              <button className="button" type="button" onClick={printDocument}>
                印刷プレビュー
              </button>
            </div>
          </aside>

          <div className="mockups-preview-column">
            <div className="mockups-preview-heading">
              <div>
                <p className="eyebrow">LIVE PREVIEW</p>
                <h2>{manifest.displayName}</h2>
              </div>
              <span>
                {catalogItem.formatName}
                {pageCount > 1 ? ` · 全${pageCount}ページ` : ''}
              </span>
            </div>
            <div
              ref={stageRef}
              className="mockups-stage"
              style={{ aspectRatio: `${canvasWidth} / ${canvasHeight}` }}
            >
              <iframe
                ref={previewRef}
                key={`${template}-${format}-${showBrand}`}
                title={`${manifest.displayName}・${catalogItem.formatName}のモック`}
                srcDoc={mockDocument}
                sandbox="allow-modals allow-same-origin"
                style={{
                  width: canvasWidth,
                  height: canvasHeight,
                  transform: `scale(${scale})`,
                }}
              />
            </div>
            <p className="mockups-caption">
              画面幅に合わせて縮小表示しています。別タブ表示と印刷プレビューでは実寸のHTMLを使用します。
              {pageCount > 1
                ? `1ページ目が表紙面、2ページ目以降は記録の全文を載せる本文ページです（全${pageCount}ページ）。`
                : ''}
            </p>
          </div>
        </section>
      </main>
    </Shell>
  );
}
